import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectorRef, ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { RelatedPartyPaymentService } from '../../services/related-party-payment.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  rowPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-related-party-payment-delete-ce',
    templateUrl: './related-party-payment-delete.component.html',
    styleUrls: ['./related-party-payment-delete.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class RelatedPartyPaymentDeleteComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // Standard component properties
  modalTitle = "Delete Payment";
  minimise = false;
  crudLoading = false;
  rowData: any = {};
  globalParams: any = {};

  constructor(
    private service: RelatedPartyPaymentService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    console.log("Delete Modal Row Data:", this.rowData);
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.globalParams = header.globalParams || {};
    const gc = header.generalConfigData || {};
    const baseUrl = gc.base_url;
    if (baseUrl) {
      this.service.setBaseUrl(baseUrl);
    }
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow) ? (rawRow[0] && typeof rawRow[0] === 'object' ? rawRow[0] : {}) : (rawRow || {});
    this.cdr.markForCheck();
  }

  /**
   * Delete payment
   */
  deletePayment(): void {
    this.crudLoading = true;
    this.cdr.markForCheck();

    const paymentData = [{ rp_seq_id: this.rowData.RP_SEQ_ID }];
    const userSettings = this.gtwApp.USER_SETTINGS || {};
    const gp = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};

    const ssoId = userSettings?.user?.sso_id || '';
    const screenKey = gp?.screen_key || '';
    const clobSettings = [{ sso_id: ssoId, screen_key: screenKey }];

    const filterParams = gp || {};

    this.service.deletePayment(paymentData, clobSettings, ssoId, this.globalParams).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.crudLoading = false;

        if (data.data?.errorMessage && data.data.errorMessage !== "null") {
          this.showError(data.data.errorMessage);
        } else {
          this.showSuccess("The Recipient was successfully deleted");

          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.markForCheck();
      },
      (error) => {
        this.crudLoading = false;
        console.error('Error deleting payment:', error);
        this.showError('Error deleting payment');
        this.cdr.markForCheck();
      }
    );
  }

  /**
   * Cancel and close modal
   */
  cancel(): void {
    this.crudLoading = false;
    // ✅ Emit 'cancel' - AngularJS directives will handle it
    this.requestGridUpdate.emit('cancel');
    this.cdr.markForCheck();
  }

  /**
   * Show success message
   */
  private showSuccess(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 4000,
      panelClass: ['success-snackbar']
    });
  }

  /**
   * Show error message
   */
  private showError(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 4000,
      panelClass: ['error-snackbar']
    });
  }
}
